import { useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, CheckIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useProducts, useProductStats, useUpdateProduct, useClearReview, useXeroItemsStatus, useSyncXero, useSyncXeroItem, useConsumableCatalog } from '../hooks/useProducts';

const money = (v, dp = 2) => (v == null ? '—' : `£${Number(v).toFixed(dp)}`);

// Item-type codes are the SKU prefix; friendly labels (best-guess, editable).
const CATEGORY_LABELS = {
  CI: 'Cable & Install', CC: 'Civil / Groundworks', HT: 'Hand Tools', HTC: 'Hand Tool Consumables',
  U: 'Uniform', HS: 'Health & Safety', HSC: 'H&S Consumables', PPE: 'PPE', OFF: 'Office',
  PT: 'Power Tools', PTC: 'Power Tool Consumables', AST: 'IT & Assets', V: 'Vehicle',
  FT: 'Fibre Tools', CF: 'CF', CPC: 'CPC', Uncategorised: 'Uncategorised',
};
// The dropdown is built by mapping this array, so a category missing from it
// can never be filtered on however many products carry it. 'ISP Stock' is the
// client-supplied ISP range, fed from the ISP SKU Manager by
// services/ispSkuProductsSync.js in tech -- first because it is the range Ops
// actually works from, and it is not a SKU-prefix type code like the rest.
const CATEGORY_ORDER = ['ISP Stock', 'CI', 'CC', 'FT', 'PT', 'PTC', 'HT', 'HTC', 'HS', 'HSC', 'PPE', 'U', 'V', 'AST', 'OFF', 'CF', 'CPC', 'Uncategorised'];
const catLabel = (c) => CATEGORY_LABELS[c] || c || 'Uncategorised';

// Cost per single item: the purchase price is per pack, so divide by pack size.
const perItem = (price, pack) => (price != null && pack && pack > 1 ? Number(price) / pack : null);

const ISSUE = {
  no_price: { label: 'No price', cls: 'bg-amber-100 text-amber-800' },
  low_confidence: { label: 'Low confidence', cls: 'bg-sky-100 text-sky-800' },
  no_code: { label: 'No code', cls: 'bg-rose-100 text-rose-800' },
};

// Per-row "Sync to Xero" control. Owns its own mutation + error state so each
// row spins and reports independently. A SKU with no item code can't be synced.
function RowSyncButton({ product }) {
  const sync = useSyncXeroItem();
  const [err, setErr] = useState(product.xero_sync_status === 'error' ? (product.xero_sync_error || 'Last sync failed') : null);
  const synced = product.xero_sync_status === 'synced';

  if (!product.item_code) {
    return <span className="text-xs text-gray-300">no code</span>;
  }

  const onClick = (e) => {
    e.stopPropagation();
    setErr(null);
    sync.mutate(product.id, {
      onSuccess: (r) => { if (r && r.ok === false) setErr(r.error || 'Sync failed'); },
      onError: (er) => setErr(er?.response?.data?.error || 'Sync failed — try again'),
    });
  };

  const base = 'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium disabled:opacity-50';
  const tone = err
    ? 'border-rose-300 text-rose-700 hover:bg-rose-50'
    : synced
      ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
      : 'border-gray-300 text-gray-600 hover:bg-gray-50';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={sync.isPending}
      title={err || (synced ? 'Synced to Xero — click to re-sync' : 'Sync this SKU to Xero')}
      className={`${base} ${tone}`}
    >
      {err
        ? <ExclamationTriangleIcon className="h-3.5 w-3.5" />
        : <ArrowPathIcon className={`h-3.5 w-3.5 ${sync.isPending ? 'animate-spin' : ''}`} />}
      {sync.isPending ? 'Syncing…' : err ? 'Retry' : synced ? 'Synced' : 'Sync'}
    </button>
  );
}

function EditDrawer({ product, onClose }) {
  const update = useUpdateProduct();
  const clearReview = useClearReview();
  const [form, setForm] = useState({
    name: product.name || '', supplier_name: product.supplier_name || '',
    purchase_unit_price: product.purchase_unit_price ?? '', pack_size: product.pack_size ?? '',
    region: product.region || '', category: product.category || '',
    tax_rate: product.tax_rate || '', purchase_account: product.purchase_account || '',
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = () => update.mutate({ id: product.id, patch: form }, { onSuccess: onClose });
  const cpi = perItem(form.purchase_unit_price, Number(form.pack_size));

  const Field = ({ label, k, type = 'text', children }) => (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-gray-500">{label}</span>
      {children || <input type={type} value={form[k]} onChange={set(k)} step={type === 'number' ? '0.01' : undefined} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />}
    </label>
  );

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <div className="h-full w-full max-w-md overflow-y-auto bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div><h2 className="text-lg font-bold text-gray-900">{product.item_code}</h2><p className="text-sm text-gray-500">{product.source === 'po_import' ? 'From purchase order' : 'Supplier SKU list'}</p></div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        {product.review_reason && <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">Review note: {product.review_reason}</div>}
        {product.xero_sync_status === 'error' && product.xero_sync_error && <div className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700">Xero: {product.xero_sync_error}</div>}
        <div className="space-y-3">
          <Field label="Name" k="name" />
          <Field label="Supplier" k="supplier_name" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Purchase price (£)" k="purchase_unit_price" type="number" />
            <Field label="Pack size (qty)" k="pack_size" type="number" />
          </div>
          {cpi != null && <p className="-mt-1 text-xs text-gray-500">Cost per item: <span className="font-semibold text-gray-700">{money(cpi, 4)}</span> ({form.pack_size} per pack)</p>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Region (cost code)" k="region">
              <select value={form.region} onChange={set('region')} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="">— none —</option><option value="N">Region N</option><option value="B">Region B</option><option value="BOTH">Both</option>
              </select>
            </Field>
            <Field label="Category (type code)" k="category" />
          </div>
          <div className="grid grid-cols-2 gap-3"><Field label="Tax rate" k="tax_rate" /><Field label="Purchase account" k="purchase_account" /></div>
        </div>
        <div className="mt-5 flex items-center gap-2">
          <button type="button" onClick={save} disabled={update.isPending} className="flex-1 rounded-md bg-brand py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50">{update.isPending ? 'Saving…' : 'Save changes'}</button>
          {product.needs_review ? <button type="button" onClick={() => clearReview.mutate(product.id, { onSuccess: onClose })} className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800"><CheckIcon className="mr-1 inline h-4 w-4" />Mark reviewed</button> : null}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <RowSyncButton product={product} />
          <button type="button" onClick={() => update.mutate({ id: product.id, patch: { is_active: product.is_active ? 0 : 1 } }, { onSuccess: onClose })} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">{product.is_active ? 'Deactivate SKU' : 'Reactivate SKU'}</button>
        </div>
      </div>
    </div>
  );
}

const STOREROOM_CATS = {
  consumables: 'Consumables', tools: 'Tools', uniform_ppe: 'Uniform & PPE', civil: 'Civils materials',
};

const STATUS_STYLE = {
  orderable: 'bg-emerald-100 text-emerald-800',
  held: 'bg-amber-100 text-amber-800',
  unquoted: 'bg-gray-100 text-gray-600',
};
const STATUS_LABEL = { orderable: 'Orderable', held: 'Price unclear', unquoted: 'No quote' };

// The storeroom half of "things we buy". Separate from the SKU catalog above
// because they are separate tables with separate ledger keys — merging them
// would give one physical item two stock balances.
function ConsumablesPanel() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const { data, isLoading } = useConsumableCatalog({ q, category });

  const rows = (data?.items || []).filter((r) => !status || r.status === status);
  const counts = data?.counts || {};

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code or description…" className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
          <option value="">All categories</option>
          {Object.entries(STOREROOM_CATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
          <option value="">All items</option>
          <option value="orderable">Orderable ({counts.orderable || 0})</option>
          <option value="held">Price unclear ({counts.held || 0})</option>
          <option value="unquoted">No quote ({counts.unquoted || 0})</option>
        </select>
        <span className="ml-auto text-sm text-gray-400">
          {counts.orderable || 0} orderable · <span className="text-amber-600">{counts.held || 0} price unclear</span> · {counts.unquoted || 0} no quote
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <th className="px-4 py-2.5">Code</th><th className="px-4 py-2.5">Description</th>
              <th className="px-4 py-2.5">Category</th><th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 text-right">Best price</th><th className="px-4 py-2.5">Quotes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">Loading…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">Nothing matches.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className={r.status === 'held' ? 'bg-amber-50/40' : undefined}>
                <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-gray-600">{r.item_code}</td>
                <td className="px-4 py-2.5 text-sm text-gray-900">{r.name}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-500">{STOREROOM_CATS[r.category] || r.category}</td>
                <td className="whitespace-nowrap px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums text-gray-800">
                  {r.best_price != null ? `£${r.best_price.toFixed(2)}` : '—'}
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-600">
                  {r.suppliers.length === 0 ? <span className="text-gray-400">nobody has quoted this</span> : (
                    <span className="flex flex-wrap gap-x-3 gap-y-1">
                      {r.suppliers.map((sup, i) => (
                        <span key={i} className={sup.usable ? '' : 'text-amber-700'}>
                          {sup.supplier} £{sup.unit_price.toFixed(2)}
                          {sup.is_preferred ? <span className="text-emerald-600"> ✓</span> : null}
                        </span>
                      ))}
                    </span>
                  )}
                  {r.hold_reason ? <span className="mt-0.5 block text-amber-700">{r.hold_reason}</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-gray-400">
        An item is orderable when it has a usable supplier price — that is the only switch.
        &quot;Price unclear&quot; means the quotes disagree on the unit (per item vs per pack),
        which has to be settled before anything can be bought at the right value.
      </p>
    </>
  );
}

function Products() {
  const [view, setView] = useState('products');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [issue, setIssue] = useState('');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState(null);
  const LIMIT = 100;

  const { data: stats } = useProductStats();
  const { data, isLoading, isError } = useProducts({ q, category, issue, limit: LIMIT, offset });
  const items = data?.items || [];
  const total = data?.total || 0;
  const reset = (fn) => (v) => { fn(v); setOffset(0); };

  const xeroStatus = useXeroItemsStatus();
  const syncXero = useSyncXero();
  const [syncMsg, setSyncMsg] = useState(null);
  const doSync = () => {
    setSyncMsg(null);
    syncXero.mutate({}, {
      onSuccess: (r) => setSyncMsg(`Synced ${r.synced}/${r.total} to Xero${r.failed ? ` · ${r.failed} failed` : ''}`),
      onError: (e) => setSyncMsg(e?.response?.data?.error || 'Sync failed — try again'),
    });
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SKU Catalog</h1>
          <p className="mt-1 text-sm text-gray-500">The master product list — the single source of truth for purchase orders and the Xero item sync.</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {stats && <p className="text-sm text-gray-400">{stats.total} SKUs · <span className="text-amber-600">{stats.no_price} need price</span> · <span className="text-sky-600">{stats.low_confidence} low-confidence</span></p>}
          <div className="flex items-center gap-3">
            {xeroStatus.data && (
              <span className="text-xs text-gray-400">
                Xero: {Number(xeroStatus.data.synced) || 0}/{Number(xeroStatus.data.total) || 0} synced
                {Number(xeroStatus.data.errors) ? ` · ${xeroStatus.data.errors} errors` : ''}
              </span>
            )}
            <button
              type="button"
              onClick={doSync}
              disabled={syncXero.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${syncXero.isPending ? 'animate-spin' : ''}`} /> {syncXero.isPending ? 'Syncing…' : 'Sync all to Xero'}
            </button>
          </div>
          {syncMsg && <span className="text-xs text-gray-500">{syncMsg}</span>}
        </div>
      </div>

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {[['products', 'Purchasing catalogue'], ['consumables', 'Storeroom consumables']].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setView(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${
              view === k ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'consumables' ? <ConsumablesPanel /> : (
      <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input value={q} onChange={(e) => reset(setQ)(e.target.value)} placeholder="Search code, name, supplier…" className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm" />
        </div>
        <select value={category} onChange={(e) => reset(setCategory)(e.target.value)} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
          <option value="">All types</option>
          {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
        </select>
        <select value={issue} onChange={(e) => reset(setIssue)(e.target.value)} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
          <option value="">All items</option>
          <option value="no_price">Needs price</option>
          <option value="low_confidence">Low confidence</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <th className="px-4 py-2.5">Item code</th><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Supplier</th><th className="px-4 py-2.5 text-right">Pack</th>
              <th className="px-4 py-2.5 text-right">Price</th><th className="px-4 py-2.5 text-right">Cost / item</th><th className="px-4 py-2.5">Flags</th>
              <th className="px-4 py-2.5">Xero</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>}
            {isError && <tr><td colSpan={9} className="px-4 py-6 text-center text-rose-500">Could not load the catalog.</td></tr>}
            {!isLoading && !isError && items.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">No SKUs match.</td></tr>}
            {items.map((p) => {
              const cpi = perItem(p.purchase_unit_price, p.pack_size);
              return (
                <tr key={p.id} onClick={() => setEditing(p)} className={`cursor-pointer hover:bg-brand-light/40 ${p.is_active ? '' : 'opacity-50'}`}>
                  <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-gray-600">{p.item_code}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-800">{p.name}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-500">{catLabel(p.category)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-600">{p.supplier_name || '—'}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums text-gray-500">{p.pack_size ?? '—'}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums text-gray-800">{money(p.purchase_unit_price)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums text-gray-600">{cpi != null ? money(cpi, 4) : (p.pack_size > 1 ? '—' : '')}</td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <span className="flex flex-wrap gap-1">
                      {ISSUE[p.data_issue] && <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ISSUE[p.data_issue].cls}`}>{ISSUE[p.data_issue].label}</span>}
                      {p.needs_review ? <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">review</span> : null}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <RowSyncButton product={p} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
        <span>{total === 0 ? '0' : `${offset + 1}–${Math.min(offset + LIMIT, total)}`} of {total}</span>
        <span className="flex gap-2">
          <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))} className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40">Prev</button>
          <button type="button" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)} className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40">Next</button>
        </span>
      </div>

      </>
      )}

      {editing && <EditDrawer product={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

export default Products;
